import type { PropertyReportTask } from "@stratify/shared";
import { limit, orderBy, Query, query } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { useFirestoreCollectionListener } from "../../../shared/hooks/useFirestoreCollectionListener";

interface UsePropertyReportTaskResult {
  reportTask: PropertyReportTask | null;
  isLoading: boolean;
  error: string | null;
  currentReportTaskId: string | null;
}

/**
 * Custom hook for managing property report generation tasks.
 * Handles the API call to trigger report generation and sets up a Firestore
 * listener to track the generation progress via the task document.
 */
export function usePropertyReportTask(
  userId: string,
  propertyId: string
): UsePropertyReportTaskResult {
  const [error, setError] = useState<string | null>(null);

  // Build a query for the latest task
  const tasksPath = `users/${userId}/properties/${propertyId}/propertyReportTasks`;
  const queryBuilder = useCallback(
    (baseQuery: Query) =>
      query(baseQuery, orderBy("createdAt", "desc"), limit(1)),
    []
  );

  const {
    data: tasks,
    error: taskError,
    isLoading,
  } = useFirestoreCollectionListener<PropertyReportTask>(
    userId && propertyId ? tasksPath : null,
    queryBuilder
  );

  // Get the latest task
  const reportTask = tasks && tasks.length > 0 ? tasks[0] : null;

  // Update error state when listener errors occur
  useEffect(() => {
    setError(taskError || null);
  }, [taskError]);

  return {
    reportTask,
    isLoading: isLoading,
    error,
    currentReportTaskId: reportTask?.id || null,
  };
}
